import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Reply, Copy, Flag, Info, Trash2, X } from "lucide-react";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * WhatsApp-style long-press / right-click context menu.
 *
 * Renders a floating popup positioned at (anchorX, anchorY). The parent
 * component owns the "which message is open?" state — this file just
 * paints the menu and fires callbacks. Auto-flips at the viewport edge
 * so a right-click near the bottom-right of the screen still shows
 * everything.
 */
export function MessageContextMenu({
  anchor, // {x, y} in viewport coords
  onClose,
  onReact,
  onReply,
  onCopy,
  onFlag,
  onInfo,
  onDelete,
  canFlag,
  canDelete,
  isComplaint,
  hasSelection,
}) {
  const rootRef = useRef(null);
  const [pos, setPos] = useState({ left: anchor.x, top: anchor.y, ready: false });

  // Position after mount so we know the popup's actual size and can flip
  // it away from any viewport edge (works with 1 or 10 menu items).
  useLayoutEffect(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.x;
    let top = anchor.y;
    if (left + rect.width + 8 > vw) left = Math.max(8, vw - rect.width - 8);
    if (top + rect.height + 8 > vh) top = Math.max(8, vh - rect.height - 8);
    setPos({ left, top, ready: true });
  }, [anchor.x, anchor.y]);

  // Close on Esc / outside click / scroll.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose();
    };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onOutside);
    window.addEventListener("touchstart", onOutside);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onOutside);
      window.removeEventListener("touchstart", onOutside);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  const item = (Icon, label, onClick, opts = {}) => (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => {
        onClick && onClick();
        onClose();
      }}
      data-testid={opts.testid}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-[#F5F5F0] transition-colors ${
        opts.destructive ? "text-[#8A6046]" : "text-[#1C1C19]"
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
      <span className="flex-1">{label}</span>
      {opts.hint && <span className="text-[10px] text-[#6B6B61]">{opts.hint}</span>}
    </button>
  );

  const menu = (
    <>
      {/* Invisible backdrop so a single tap outside closes on mobile */}
      <div
        className="fixed inset-0 z-[100]"
        onMouseDown={onClose}
        onTouchStart={onClose}
      />
      <div
        ref={rootRef}
        role="menu"
        data-testid="chat-message-context-menu"
        style={{
          position: "fixed",
          left: pos.left,
          top: pos.top,
          visibility: pos.ready ? "visible" : "hidden",
          zIndex: 101,
          minWidth: 220,
        }}
        className="bg-white border border-[#DCDCD3] shadow-2xl rounded-xl overflow-hidden animate-in fade-in-0 zoom-in-95"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* Emoji reaction bar (top) */}
        <div className="flex items-center gap-1 px-2 py-2 border-b border-[#F5F5F0] bg-[#FAFAF6]">
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onReact && onReact(emoji);
                onClose();
              }}
              data-testid={`ctx-emoji-${emoji}`}
              className="text-lg hover:scale-125 transition-transform p-1 rounded-full hover:bg-white"
            >
              {emoji}
            </button>
          ))}
          <button
            type="button"
            onClick={onClose}
            data-testid="ctx-close"
            className="ml-auto p-1 rounded-full hover:bg-black/5 text-[#6B6B61]"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
        <div className="py-1">
          {item(Reply, hasSelection ? "Reply to selection" : "Reply", onReply, { testid: "ctx-reply" })}
          {item(Copy, hasSelection ? "Copy selection" : "Copy", onCopy, { testid: "ctx-copy" })}
          {item(Info, "Message info", onInfo, { testid: "ctx-info" })}
          {canDelete && item(Trash2, "Delete for everyone", onDelete, {
            testid: "ctx-delete",
            destructive: true,
          })}
        </div>
      </div>
    </>
  );

  return createPortal(menu, document.body);
}
